import React, { useEffect, useRef, useState } from "react";
import {
  Inventory2Outlined,
  CheckCircleOutlineOutlined,
  RefreshOutlined,
  SearchOutlined,
} from "@mui/icons-material";

const ProductList = ({
  cardStyle,
  products = [],
  selectedProduct,
  setSelectedProduct,
  onProductSelect,
  onSearch,
  loading = false,
  onRefresh,
}) => {
  // ============================================================
  // THEME
  // ============================================================

  const THEME = {
    primary: "#6D28D9",
    primaryDark: "#5B21B6",
    accent: "#8B5CF6",

    light: "#F5F3FF",
    lighter: "#FAF9FF",

    border: "#DDD6FE",
    iconBg: "#EDE9FE",

    text: "#334155",
    muted: "#64748B",
    lightText: "#94A3B8",

    success: "#059669",
  };

  const [searchQuery, setSearchQuery] = useState("");
  const onSearchRef = useRef(onSearch);
  const hasSearched = useRef(false);

  // Keep the latest parent callback without re-running the debounce when the
  // parent re-renders.
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  useEffect(() => {
    if (!hasSearched.current) return;

    const timer = setTimeout(() => {
      onSearchRef.current?.(searchQuery.trim());
    }, 350);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ============================================================
  // PRODUCT SELECT
  // ============================================================

  const handleProductSelect = (product) => {
    if (onProductSelect) {
      onProductSelect(product);
      return;
    }

    setSelectedProduct(product);
  };

  const handleProductSearch = (e) => {
    hasSearched.current = true;
    setSearchQuery(e.target.value);
  };

  return (
    <div
      style={{
        width: "100%",
        minWidth: 0,
      }}
    >
      <div
        style={{
          ...cardStyle,
          overflow: "hidden",
          position: "sticky",
          top: "15px",
          background: "#FFFFFF",
        }}
      >
        {/* ======================================================
            HEADER
        ====================================================== */}

        <div
          style={{
            minHeight: "58px",
            padding: "0 15px",
            borderBottom: "1px solid #E2E8F0",

            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",

            background: "#FFFFFF",
          }}
        >
          {/* LEFT */}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "9px",
              minWidth: 0,
            }}
          >
            <div
              style={{
                width: "34px",
                height: "34px",
                minWidth: "34px",

                borderRadius: "8px",

                background: THEME.iconBg,

                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Inventory2Outlined
                style={{
                  fontSize: "19px",
                  color: THEME.primary,
                }}
              />
            </div>

            <div
              style={{
                minWidth: 0,
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: THEME.text,
                  lineHeight: 1.2,
                }}
              >
                Product List
              </div>

              <div
                style={{
                  fontSize: "10px",
                  color: THEME.lightText,
                  marginTop: "3px",
                }}
              >
                Select a product to configure
              </div>
            </div>
          </div>

          {/* RIGHT */}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            {/* REFRESH */}

            {onRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                disabled={loading}
                title="Refresh products"
                style={{
                  width: "30px",
                  height: "30px",

                  border: "1px solid #E2E8F0",
                  borderRadius: "7px",

                  background: "#FFFFFF",

                  color: THEME.muted,

                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",

                  cursor: loading ? "not-allowed" : "pointer",

                  opacity: loading ? 0.5 : 1,
                }}
              >
                <RefreshOutlined
                  style={{
                    fontSize: "17px",
                  }}
                />
              </button>
            )}

            {/* COUNT */}

            {/* <div
              style={{
                minWidth: "32px",
                height: "30px",

                padding: "0 8px",

                borderRadius: "7px",

                background: THEME.light,
                color: THEME.primary,

                display: "flex",
                alignItems: "center",
                justifyContent: "center",

                fontSize: "11px",
                fontWeight: 700,

                boxSizing: "border-box",
              }}
            >
              {products.length}
            </div> */}
          </div>
        </div>

        <div
          style={{
            padding: "9px",
            maxHeight: "calc(100vh - 150px)",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              position: "relative",
              width: "100%",
              marginBottom: "10px",
            }}
          >
            {/* SEARCH ICON */}
            <SearchOutlined
              style={{
                position: "absolute",
                left: "11px",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "17px",
                color: "#8b5cf6",
                pointerEvents: "none",
                zIndex: 1,
              }}
            />

            {/* SEARCH INPUT */}
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={handleProductSearch}
              style={{
                width: "100%",
                height: "38px",
                padding: "0 36px 0 34px",
                boxSizing: "border-box",
                border: "1px solid #ddd6fe",
                borderRadius: "7px",
                background: "#faf9ff",
                outline: "none",
                fontSize: "12px",
                fontWeight: 500,
                color: "#334155",
                transition: "all 0.15s ease",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#8b5cf6";
                e.target.style.background = "#ffffff";
                e.target.style.boxShadow = "0 0 0 2px rgba(139, 92, 246, 0.10)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#ddd6fe";
                e.target.style.background = "#faf9ff";
                e.target.style.boxShadow = "none";
              }}
            />

            {/* SEARCH SHORTCUT */}
            <span
              style={{
                position: "absolute",
                right: "9px",
                top: "50%",
                transform: "translateY(-50%)",
                padding: "2px 6px",
                borderRadius: "4px",
                border: "1px solid #e2e8f0",
                background: "#ffffff",
                color: "#94a3b8",
                fontSize: "8px",
                fontWeight: 600,
                pointerEvents: "none",
              }}
            >
              Search
            </span>
          </div>
          {loading && (
            <div
              style={{
                padding: "45px 15px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",

                  margin: "0 auto 10px",

                  borderRadius: "50%",

                  border: `3px solid ${THEME.light}`,
                  borderTopColor: THEME.primary,

                  animation: "productListSpin 0.8s linear infinite",
                }}
              />

              <div
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: THEME.muted,
                }}
              >
                Loading products...
              </div>
            </div>
          )}

          {!loading &&
            products.length > 0 &&
            products.map((product, index) => {
              const productId = product._id;
              const isSelected = selectedProduct?._id === productId;
              return (
                <div
                  key={productId}
                  onClick={() => handleProductSelect(product)}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "11px",
                    marginBottom: index < products.length - 1 ? "7px" : "0",
                    border: isSelected
                      ? `1px solid ${THEME.primary}`
                      : "1px solid #E2E8F0",
                    borderRadius: "9px",
                    background: isSelected ? THEME.light : "#FFFFFF",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    {/* =================================================
                        NUMBER
                    ================================================== */}

                    <div
                      style={{
                        width: "31px",
                        height: "31px",
                        minWidth: "31px",

                        borderRadius: "7px",

                        background: isSelected ? THEME.primary : "#F1F5F9",

                        color: isSelected ? "#FFFFFF" : THEME.muted,

                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",

                        fontSize: "11px",
                        fontWeight: 700,

                        transition: "all 0.15s ease",
                      }}
                    >
                      {index + 1}
                    </div>

                    {/* =================================================
                        PRODUCT INFORMATION
                    ================================================== */}

                    <div
                      style={{
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      {/* PRODUCT NAME */}

                      <div
                        style={{
                          fontSize: "13px",

                          fontWeight: 700,

                          color: isSelected ? THEME.primaryDark : THEME.text,

                          whiteSpace: "nowrap",

                          overflow: "hidden",

                          textOverflow: "ellipsis",

                          lineHeight: 1.3,
                        }}
                      >
                        {product.name}
                      </div>

                      {/* PRODUCT META */}

                      <div
                        style={{
                          display: "flex",

                          alignItems: "center",

                          flexWrap: "wrap",

                          gap: "5px",

                          marginTop: "4px",

                          fontSize: "10px",

                          color: THEME.lightText,
                        }}
                      >
                        {/* UNIT */}

                        <span
                          style={{
                            fontWeight: 600,

                            color: isSelected ? THEME.accent : THEME.muted,
                          }}
                        >
                          {product.unit1 || "Unit"}
                        </span>

                        {/* UNIT 2 */}

                        {product.unit2 && product.unit2 !== product.unit1 && (
                          <>
                            <span>•</span>

                            <span>{product.unit2}</span>
                          </>
                        )}

                        {/* TAX */}

                        <span>•</span>

                        <span>{product.tax ?? 0}% Tax</span>
                      </div>

                      {/* CONVERSION */}
                    </div>

                    {/* =================================================
                        RIGHT SIDE
                    ================================================== */}

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",

                        flexShrink: 0,
                      }}
                    >
                      {isSelected && (
                        <CheckCircleOutlineOutlined
                          style={{
                            fontSize: "19px",
                            color: THEME.accent,
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

          {/* ====================================================
              EMPTY LIST
          ==================================================== */}

          {!loading && products.length === 0 && (
            <div
              style={{
                padding: "45px 15px",

                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: "52px",
                  height: "52px",

                  margin: "0 auto 11px",

                  borderRadius: "50%",

                  background: THEME.light,

                  display: "flex",

                  alignItems: "center",

                  justifyContent: "center",
                }}
              >
                <Inventory2Outlined
                  style={{
                    fontSize: "24px",
                    color: THEME.accent,
                  }}
                />
              </div>

              <div
                style={{
                  fontSize: "12px",

                  fontWeight: 700,

                  color: THEME.muted,
                }}
              >
                No products found
              </div>

              <div
                style={{
                  marginTop: "4px",

                  fontSize: "9px",

                  color: THEME.lightText,

                  lineHeight: 1.5,
                }}
              >
                Add a product to get started.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ============================================================
          SPINNER ANIMATION
      ============================================================ */}

      <style>
        {`
          @keyframes productListSpin {
            from {
              transform: rotate(0deg);
            }

            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    </div>
  );
};

export default ProductList;
